class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector('.m-cart-drawer__inner');
    this.cartDrawerCloseIcon = this.querySelector('.m-cart-drawer__close');
    this.rootUrl = window.Shopify.routes.root;
    this.activeElement = null;

    this.setHeaderCartIconAccessibility();
    this.bindDrawerEvents();
  }

  bindDrawerEvents() {
    this.cartDrawerCloseIcon?.addEventListener('click', () => this.close());

    this.addEventListener('click', (event) => {
      if (!event.target.closest('.m-cart-drawer__inner')) {
        this.close();
      }
    });

    this.addEventListener('keydown', (event) => {
      if (!this.classList.contains('m-cart-drawer--active')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('.m-cart-icon-bubble');

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', this.id);
      cartLink.setAttribute('aria-expanded', 'false');

      cartLink.addEventListener('click', (event) => {
        if (window.MinimogSettings?.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  closeMobileMenuIfNeeded() {
    const mobileMenu = document.querySelector('[data-md-mobile-menu].is-open');
    if (!mobileMenu) return;

    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    mobileMenu.setAttribute('inert', '');
    document.querySelector('[data-md-menu-toggle]')?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('md-menu-open');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.closeMobileMenuIfNeeded();
    this.classList.add('m-cart-drawer--active');

    requestAnimationFrame(() => {
      this.style.setProperty('--m-bg-opacity', '0.5');
      this.cartDrawerInner?.style.setProperty('--translate-x', '0');
      this.cartDrawerCloseIcon?.focus({ preventScroll: true });
    });

    document.querySelectorAll('.m-cart-icon-bubble').forEach((cartLink) => {
      cartLink.setAttribute('aria-expanded', 'true');
    });

    window.MinimogEvents?.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add('prevent-scroll');
  }

  close() {
    this.style.setProperty('--m-bg-opacity', '0');
    this.cartDrawerInner?.style.setProperty('--translate-x', '100%');

    document.querySelectorAll('.m-cart-icon-bubble').forEach((cartLink) => {
      cartLink.setAttribute('aria-expanded', 'false');
    });

    window.setTimeout(() => {
      this.classList.remove('m-cart-drawer--active');
      document.documentElement.classList.remove('prevent-scroll');

      if (this.activeElement instanceof HTMLElement) {
        this.activeElement.focus({ preventScroll: true });
      }
    }, 300);
  }

  renderContents(parsedState) {
    this.classList.remove('m-cart--empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.querySelector(section.selector);
      const newContent = this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector);
      if (sectionElement && newContent !== null) {
        sectionElement.innerHTML = newContent;
      }
    });

    window.setTimeout(() => this.open(), 0);
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll('.m-cart-count-bubble');

    cartCounts.forEach((cartCount) => {
      cartCount.textContent = itemCount;
      cartCount.classList.toggle('m:hidden', itemCount <= 0);
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + 'cart.json');
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${window.MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          if (!updateFooter && section.block === 'cart-footer') return;

          const sectionElement = document.querySelector(section.selector);
          const newContent = this.getSectionInnerHTML(responseText, section.selector);
          if (sectionElement && newContent !== null) {
            sectionElement.innerHTML = newContent;
          }
        });
      })
      .catch((error) => console.error(error));

    this.getCart().then((cart) => {
      this.classList.toggle('m-cart--empty', cart.item_count === 0);
      this.updateCartCount(cart.item_count);
    });
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '[data-md-cart-title]',
        block: 'cart-header'
      },
      {
        id: 'cart-drawer',
        selector: '[data-minimog-cart-items]',
        block: 'cart-items'
      },
      {
        id: 'cart-drawer',
        selector: '[data-minimog-cart-discounts]',
        block: 'cart-footer'
      },
      {
        id: 'cart-drawer',
        selector: '[data-md-cart-total]',
        block: 'cart-footer'
      }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('m-cart-drawer')) {
  customElements.define('m-cart-drawer', MCartDrawer);
}

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: 'MinimogCartDrawer',
        section: 'cart-drawer',
        selector: '[data-md-cart-title]'
      },
      {
        id: 'MinimogCartDrawer',
        section: 'cart-drawer',
        selector: '[data-minimog-cart-items]'
      },
      {
        id: 'MinimogCartDrawer',
        section: 'cart-drawer',
        selector: '[data-minimog-cart-discounts]'
      },
      {
        id: 'MinimogCartDrawer',
        section: 'cart-drawer',
        selector: '[data-md-cart-total]'
      }
    ];
  }
}

if (!customElements.get('m-cart-drawer-items')) {
  customElements.define('m-cart-drawer-items', MCartDrawerItems);
}
